import Tool from '@/assets/js/utils'
import * as Sentry from '@sentry/vue'
import Vue from 'vue'
import router from '../../router'

/**
 * 初始化Sentry监控
 * @param {string} email 用户邮箱用于标识用户
 */
export function initSentry (email) {
  if (window.Sentry) return

  const env = Tool.getEnvironment()
  const isProductionEnv = env !== 'development' && Tool.checkIsCom()

  if (!isProductionEnv) return

  Sentry.init({
    Vue,
    dsn: SENTRY_CONFIG.DSN,
    ignoreErrors: SENTRY_CONFIG.IGNORE_ERRORS,
    denyUrls: SENTRY_CONFIG.DENY_URLS,
    replaysOnErrorSampleRate: SENTRY_CONFIG.SAMPLE_RATES.replays,
    environment: env,
    release: localStorage.getItem('newCCVersion') || 'unknown',
    trackComponents: false,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: true, // 屏蔽文本内容，降低内存开销
        blockAllMedia: true, // 阻止媒体元素录制（视频类应用内存优化关键项）
        networkCaptureBodies: false, // 不录制请求/响应 body，减少内存占用
        maxReplayDuration: 5 * 60 * 1000, // 最长录制 5 分钟，防止长时间占用内存
        // Vue 2 特定配置
        _experiments: {
          captureExceptions: false // 避免与 Vue 2 错误处理冲突
        }
      })
    ],
    sampleRate: SENTRY_CONFIG.SAMPLE_RATES.errors,
    // tracesSampleRate: SENTRY_CONFIG.SAMPLE_RATES.traces,
    tracesSampler: samplingContext => {
      // 1. 按路由区分采样率
      const { transactionContext } = samplingContext
      // 4. 慢事务优先采样
      if (transactionContext.data?.responseTime > 3000) return 0.5 // 超过3秒的事务50%采样
      // 静态资源等非关键路径极低采样
      if (transactionContext.name.match(/\.[css|js|png|jpg]/)) return 0.001 // 0.1%
      return 0.01
    },
    _experiments: {
      // 减少 spans 数量
      enableSpanExperimentalDefaults: false,
      // 关闭不必要的 instrumentation
      disableBrowserTracingSpan: true
    },
    tracePropagationTargets: SENTRY_CONFIG.TRACE_PROPAGATION_TARGETS,
    beforeSendTransaction (event) {
      if (event.transaction === 'Vue <<Root>>') {
        return null // 丢弃Root组件transaction
      }
      return event
    },
    beforeSend (event, hint) {
      const error = hint?.originalException
      if (error?.message && SENTRY_CONFIG.IGNORE_ERRORS.some(
        pattern => error.message.includes(pattern))) {
        return null
      }
      // sortablejs 拖拽结束与组件销毁竞态：drop/dragend 触发 off() 时元素已为 null。
      // Chrome / Firefox / Safari 的报错文案各不相同，仅靠 ignoreErrors 字符串会漏报，
      // 这里按「removeEventListener + sortablejs 堆栈帧」精准判定丢弃，不误伤其它报错。
      if (isSortableTeardownRace(event, error)) {
        return null
      }
      return event
    }
  })

  // 设置用户信息
  Sentry.setUser({ email })
  window.Sentry = Sentry
}
// 常量配置
const SENTRY_CONFIG = {
  DSN: 'https://0ee1e71fe34b9dbd6659b40265eadc50@o982456.ingest.us.sentry.io/4507846950846464',
  IGNORE_ERRORS: [
    'Loading CSS chunk', 'NotSupportedError', 'NotAllowedError',
    'InvalidStateError', 'SecurityError', 'AgoraRTCError',
    'RTCPeerConnection', 'Network Error', 'ResizeObserver',
    'aborted', '401', '403', '404', '500', '504',
    // 插件/扩展相关错误
    'top.GLOBALS', 'originalCreateNotification', 'canvas.contentDocument',
    'MyApp_RemoveAllHighlights', 'http://tt.epicplay.com',
    "Can't find variable: ZiteReader", 'jigsaw is not defined',
    'ComboSearch is not defined', 'http://loading.retry.widdit.com/',
    'atomicFindClose', 'fb_xd_fragment', 'bmi_SafeAddOnload',
    'EBCallBackMessageReceived', 'conduitPage',

    // 浏览器/网络相关
    'Failed to fetch',
    'NetworkError when attempting to fetch resource',
    'Timeout',
    'Request aborted',
    'The operation was aborted',
    'The user aborted a request',
    'The request was aborted',
    'Load failed',
    'ERR_CONNECTION_REFUSED',
    'ERR_CONNECTION_RESET',
    'ERR_CONNECTION_CLOSED',
    'ERR_EMPTY_RESPONSE',
    'ERR_NETWORK_CHANGED',

    // WebRTC 相关
    'ICE failed',
    'DTLS transport connection failed',
    'RTCIceTransport failed',
    'RTCPeerConnection ice connection failed',

    // 媒体相关
    'The play() request was interrupted',
    'The element has no supported sources',
    'The element was paused',
    'VIDEOJS: ERROR: (CODE:4 MEDIA_ERR_SRC_NOT_SUPPORTED)',

    // 广告拦截相关
    'adsbygoogle',
    'googletag',
    'adblock',

    // 浏览器扩展相关
    'Extension context invalidated',
    'Chrome extension',
    'Firefox extension',
    // Agora SDK 调用 chrome.runtime.sendMessage() 请求屏幕共享扩展时，
    // 若扩展未安装，Chrome 运行时抛出此错误；属外部浏览器行为，无需处理。
    'Invalid call to runtime.sendMessage',
    'Tab not found',

    // 第三方库特定错误
    'Bootstrap',
    'Vue warn',
    'vuex',
    // @sentry/vue 组件追踪 mixin(tracing.ts formatComponentName/classify) 与 Vue updated 钩子
    // 相互触发调度更新，导致栈溢出。属 SDK 内部递归，无业务代码根因，各浏览器文案不同：
    //   Firefox: too much recursion
    //   Chrome/Safari: Maximum call stack size exceeded
    // 参见 Sentry CC-3-0-UI-9Z
    'too much recursion',
    'Maximum call stack size exceeded',

    // 浏览器特定问题
    'QuotaExceededError',
    'NS_ERROR_NOT_INITIALIZED',
    'NS_ERROR_FAILURE',
    'NS_ERROR_DOM_BAD_URI',
    'NS_ERROR_NOT_AVAILABLE',

    // 移动设备特定问题
    'WebKit encountered an internal error',
    'WebKitBlobResource',

    // 用户行为相关
    'Script error.',
    'Uncaught TypeError: Cannot read property',
    'Uncaught ReferenceError:',
    'UnhandledRejection',
    // sortablejs 组件销毁时的 removeEventListener 竞态（第三方库，无业务影响）
    "Cannot read properties of null (reading 'removeEventListener')",

    // 性能监控相关
    'ResourceLoad',
    'LongTask',

    // Sentry Replay 自身内存问题（rrweb 在复杂 DOM 页面可能 OOM）
    'out of memory',
    'Out of memory',

    // 浏览器兼容性问题
    'Object doesn\'t support property or method',
    'Unexpected token',
    'SyntaxError',

    // WebSocket 相关
    'WebSocket is already in CLOSING or CLOSED state',
    'WebSocket connection failed',

    // 地理位置相关
    'Geolocation permission denied',
    'Geolocation position unavailable',

    // 缓存相关
    'Cache storage quota exceeded',
    'Application cache',

    // 浏览器插件/开发者工具
    'DevTools',
    'React DevTools',
    'Vue DevTools'
  ],
  DENY_URLS: [
    /graph\.facebook\.com/i,
    /connect\.facebook\.net\/en_US\/all\.js/i,
    /eatdifferent\.com\.woopra-ns\.com/i,
    /static\.woopra\.com\/js\/woopra\.js/i,
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /127\.0\.0\.1:4001\/isrunning/i,
    /webappstoolbarba\.texthelp\.com\//i,
    /metrics\.itunes\.apple\.com\.edgesuite\.net\//i
  ],
  TRACE_PROPAGATION_TARGETS: [
    'cc-ms-new.tvunetworks.com',
    'cc-ms-new-test.tvunetworks.com',
    'cc-new-dev.tvunetworks.com',
    /^\//
  ],
  SAMPLE_RATES: {
    errors: 0.5, // 错误采样率
    traces: 0.01, // 交易采样率
    replays: 1.0 // 错误时replay采样率
  }
}

// sortablejs(vuedraggable) 拖拽结束与组件销毁竞态，off() 在元素已为 null 时调用 removeEventListener。
// 各浏览器文案不同：
//   Chrome  : Cannot read properties of null (reading 'removeEventListener')
//   Firefox : can't access property "removeEventListener", <x> is null
//   Safari  : null is not an object (evaluating '<x>.removeEventListener')
// beforeSend 运行在符号化之前，堆栈是压缩 chunk，无法靠文件名识别，故按消息正则统一判定。
const REMOVE_EVENT_LISTENER_NULL_RE = /can't access property "removeEventListener"|Cannot read propert(?:y|ies) of (?:null|undefined) \(reading 'removeEventListener'\)|Cannot read property 'removeEventListener' of (?:null|undefined)|(?:null|undefined) is not an object \(evaluating '[^']*removeEventListener'\)/i

function isSortableTeardownRace (event, error) {
  const msg = (error && error.message) || (event && event.message) || ''
  return REMOVE_EVENT_LISTENER_NULL_RE.test(msg)
}
